import React, { useState } from "react";
import { createComponent } from "@lit/react";
import { useTranslation } from "react-i18next";

import { Icon } from "../icon/Icon";

import { MdOutlinedTextField } from "@material/web/textfield/outlined-text-field";

import styles from "./OutlinedTextField.module.scss";
import { submitForm } from "../../form/Form";
import { useInterpolatedPageStrings, usePageStrings } from "../../../hooks";
import { Text } from "../../text/Text";

const OutlinedTextFieldComponent = createComponent({
  tagName: "md-outlined-text-field",
  elementClass: MdOutlinedTextField,
  react: React,
  events: {
    onChange: "change",
    onInvalid: "invalid",
    onInput: "input",
    onKeyPress: "keypress",
  },
});

interface IComponentProperties {
  id?: string;
  autocomplete?: string;
  disabled?: boolean | undefined; // Apply Disabled state
  label: string; // Defines label text
  name: string; // Defines input name used when the form is submitted
  placeholder: string; // Defines placeholder text
  required?: boolean | undefined; // Apply Required
  rows?: number | undefined; // Textareas can specify the initial number of rows
  supportingText?: string | undefined; // Supporting text
  type?:
    | "text"
    | "email"
    | "number"
    | "password"
    | "search"
    | "tel"
    | "url"
    | "textarea"
    | undefined; // Defines input type
  value?: string | undefined; // Defines input value
  onChange?: (e: any) => void; // Defines onChange event handler
  onInput?: (e: any) => void;
  formid?: string | undefined;
  isError?: boolean;
  errorText?: string;
  pattern?: string;
  maxLength?: number;
  userDefinedNote?:string;
}

function ErrorTrailingIcon(isInvalid: boolean) {
  if (!isInvalid) {
    return <></>;
  }

  return (
    <span
      className={styles["OutlinedTextField-errorTrailingIcon"]}
      slot="trailing-icon"
    >
      <Icon name="error" ariaHidden={true}></Icon>
    </span>
  );
}

export function OutlinedTextField(props: IComponentProperties) {
  const [isInvalid, setIsInvalid] = useState(false);
  const [value, setValue] = useState(props.value || "");
  const { t } = useTranslation();  
  const pageStrings = usePageStrings();

  const patternErrorText:string = useInterpolatedPageStrings(pageStrings.createNewUserPage.errorMessage_passwordIncorrectFormat,[props.label]);
  

  // Events
  function handleChange(e: any) {
    const inputElement: HTMLInputElement = e.target;

    setValue(inputElement.value);

    // Set custom validity based on props.isError
    if (props.isError) {
      inputElement.setCustomValidity(props.errorText ?? "");
    } else {
      inputElement.setCustomValidity(""); // Clear custom validity
    }

    // Trigger validation to reflect changes in validity
    inputElement.reportValidity();

    // Update invalid state
    setIsInvalid(!inputElement.checkValidity());

    if (props.onChange) {
      props.onChange(e);
    }
  }

  function handleInput(e: any) {
    const inputElement: HTMLInputElement = e.target;

    if (props.onInput) {
      props.onInput(e);
    }

    setValue(inputElement.value);
console.log('isError',props.isError)
    if (props.isError) {
      inputElement.setCustomValidity(props.errorText ?? "");
      
    } else {
      inputElement.setCustomValidity("");
    }

    setIsInvalid(false);

    e.target.reportValidity();
  }

  function handleKeyPress(e: any) {
    if ((e.code === "Enter" || e.code === "NumpadEnter") && props.formid) {
      submitForm(props.formid);
    }
  }

  function handleInvalid(e: any) {
    const inputElement: HTMLInputElement = e.target;
    const validityState: ValidityState = inputElement.validity;

    if (validityState.valueMissing) {
      inputElement.setCustomValidity("Required");
    }
    else if(validityState.patternMismatch)
    {        
      inputElement.setCustomValidity(patternErrorText);
    }

    setIsInvalid(true);
  }

  return (
    <>
    
      <div className={styles["OutlinedTextField-component"]}>
        <div className={styles["OutlinedTextField-container"]}>
          <OutlinedTextFieldComponent
            id={props.id}
            label={t(props.label)}
            className={`${styles["OutlinedTextField"]}`}
            aria-label={t(props.label)}
            value={value}
            type={props.type}
            rows={props.rows}
            required={props.required}
            supportingText={t(props.supportingText||'')}
            name={props.name}
            disabled={props.disabled === true}
            placeholder={t(props.placeholder)}
            onChange={handleChange}
            onInvalid={handleInvalid}
            onInput={handleInput}
            onKeyPress={handleKeyPress}
            hasTrailingIcon={isInvalid}
            autocomplete={props.autocomplete}
            error={props.isError}
            errorText={props.errorText}
            pattern={props.pattern}
            maxLength={props.maxLength}
          >
            {ErrorTrailingIcon(isInvalid)}
          </OutlinedTextFieldComponent>
        </div>
        <div className={styles["OutlinedTextField-userDefinedNote"]}>
        <Text color='on-surface' fontStyle="body-small" value={t(props.userDefinedNote||'')}></Text>       

        </div>
      </div>
    </>
  );
}
